import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import Animated, { FadeInRight, FadeOutLeft } from "react-native-reanimated";
import { YStack } from "tamagui";
import { usePlanNavigation } from "../../../plans/usePlanNavigation";
import { Screen } from "../../../ui/Screen";
import { useStores } from "../../stores";
import { CreateWizardState, CreateWizardStep } from "./CreateWizardState";

type Props = NativeStackScreenProps<ReactNavigation.RootParamList, "Create">;

const StepView: React.FC<{ state: CreateWizardState }> = observer((props) => {
  const Component = props.state.getActiveStep().component;
  return (
    <Animated.View exiting={FadeOutLeft} entering={FadeInRight.delay(250)}>
      <YStack gap={"$medium"}>
        <Component state={props.state} />
      </YStack>
    </Animated.View>
  );
});

export const CreateWizard: React.FC<Props> = observer((_props) => {
  const { sessionStore } = useStores();

  const navigation = usePlanNavigation();

  const [state] = useState(
    () =>
      new CreateWizardState({
        initialDisplayName: sessionStore.superUser?.data.displayName,
        handleNavigate: (stepId: string, replace?: boolean) => {
          const newPath = `/plan/create${stepId ? `/${stepId}` : ""}`;
          if (replace) {
            // HACK: Last in callstack to avoid react-navigation messing with us on reload
            setTimeout(() => {
              window.history.replaceState({}, "", newPath);
            }, 0);
          } else {
            window.history.pushState({}, "", newPath);
          }
        },
      }),
  );

  useEffect(() => {
    const unsubscribe = navigation.addListener("beforeRemove", (e) => {
      if (state.activeStep !== CreateWizardStep.CREATE_USER) {
        e.preventDefault();
      }
    });

    return unsubscribe;
  }, [state, navigation]);

  return (
    <Screen>
      <StepView key={state.getActiveStep().title} state={state} />
    </Screen>
  );
});
